'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

import FitScoreBadge from '@/components/ui/FitScoreBadge';

import PlatformIcons from '@/components/ui/PlatformIcons';
import Icon from '@/components/ui/AppIcon';
import type { CampaignFormData } from './CampaignBuilderClient';

const recommendations = [
{
  id: 'rec-yasmine', fitScore: 94, name: 'Yasmine Belkacem', handle: '@yasmine.create',
  category: 'Lifestyle · Beauty', city: 'Alger', momentum: 94, engagement: 8.7,
  followers: '284K', platforms: ['instagram', 'tiktok'] as const,
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_103b528db-1763293982935.png",
  avatarAlt: 'Yasmine Belkacem creator profile photo',
  matchReasons: ['Audience 78% DZ', 'Lifestyle & Beauty', 'Disponible', 'Certifiée 360'],
  estimatedReach: '1.2M', contentAlloc: '2 Reels · 8 Stories'
},
{
  id: 'rec-amel', fitScore: 91, name: 'Amel Bensalem', handle: '@amel.bensalem',
  category: 'Beauty', city: 'Alger', momentum: 88, engagement: 9.1,
  followers: '156K', platforms: ['instagram', 'tiktok'] as const,
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1722a21c9-1772219085230.png",
  avatarAlt: 'Amel Bensalem creator profile photo',
  matchReasons: ['Engagement 9.1%', 'Audience DZ 85%', 'UGC expert', 'Disponible'],
  estimatedReach: '620K', contentAlloc: '2 Reels · 5 Stories · 2 UGC'
},
{
  id: 'rec-sarah', fitScore: 87, name: 'Sarah Hamdi', handle: '@sarah.h',
  category: 'Lifestyle · Family', city: 'Oran', momentum: 87, engagement: 7.2,
  followers: '182K', platforms: ['instagram', 'tiktok'] as const,
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4d86f5c-1774602087878.png",
  avatarAlt: 'Sarah Hamdi creator profile photo',
  matchReasons: ['Audience DZ 91%', 'Lifestyle match', 'Certifiée 360', 'Disponible'],
  estimatedReach: '730K', contentAlloc: '1 Reel · 2 Stories'
},
{
  id: 'rec-lina', fitScore: 84, name: 'Lina Meziane', handle: '@lina.beauty',
  category: 'Beauty · Fashion', city: 'Constantine', momentum: 91, engagement: 9.4,
  followers: '317K', platforms: ['instagram', 'tiktok', 'snapchat'] as const,
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_103b528db-1763293982935.png",
  avatarAlt: 'Lina Meziane creator profile photo',
  matchReasons: ['Top engagement 9.4%', 'Beauty leader', 'Multi-platform'],
  estimatedReach: '1.8M', contentAlloc: '2 Reels · 5 Stories',
  note: 'Actuellement occupée — disponible dans 2 semaines'
}];


interface Props {
  formData: CampaignFormData;
  onReset: () => void;
}

export default function CampaignRecommendations({ formData, onReset }: Props) {
  const [savedCampaign, setSavedCampaign] = useState(false);

  const totalReach = '4.35M';
  const totalBudget = recommendations.length * (formData.budgetPerCreator || 120000);

  return (
    <div className="space-y-8">
      {/* Success Header */}
      <div className="bg-positive/10 border border-positive/20 rounded-2xl p-6 flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-positive/20 flex items-center justify-center flex-shrink-0">
          <Icon name="CheckCircleIcon" size={24} className="text-positive" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-700 text-foreground">Recommended Creator Mix</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Basé sur votre brief {formData.brandName} — {recommendations.length} créateurs sélectionnés
          </p>
          <div className="flex items-center gap-4 mt-3">
            <div>
              <p className="text-xs text-muted-foreground">Reach total estimé</p>
              <p className="text-lg font-800 text-positive font-mono">{totalReach}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Budget alloué</p>
              <p className="text-lg font-800 text-foreground font-mono">{totalBudget.toLocaleString('fr-DZ')} DA</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Fit Score moyen</p>
              <p className="text-lg font-800 text-primary font-mono">89%</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSavedCampaign(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-600 transition-all active:scale-95 ${
            savedCampaign ?
            'bg-positive/15 text-positive border border-positive/30' : 'bg-primary hover:bg-primary/90 text-primary-foreground'}`
            }>
            
            <Icon name={savedCampaign ? 'CheckIcon' : 'BookmarkIcon'} size={16} />
            {savedCampaign ? 'Campagne sauvegardée' : 'Sauvegarder la campagne'}
          </button>
        </div>
      </div>

      {/* Creator Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {recommendations.map((rec, idx) =>
        <div
          key={rec.id}
          className={`bg-card border rounded-xl p-5 relative transition-all ${
          idx === 0 ? 'border-primary/60 card-glow-primary' : 'border-border'}`
          }>
          
            {idx === 0 &&
          <div className="absolute -top-2 left-4 bg-primary text-primary-foreground text-xs font-700 px-2.5 py-1 rounded-full">
                #1 Best Match
              </div>
          }

            <div className="flex items-center justify-between mb-4 mt-1">
              <div className="story-ring-primary p-0.5 rounded-full">
                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-background">
                  <AppImage src={rec.avatar} alt={rec.avatarAlt} width={40} height={40} className="w-full h-full object-cover" />
                </div>
              </div>
              <FitScoreBadge score={rec.fitScore} size="md" />
            </div>

            <p className="text-sm font-700 text-foreground">{rec.name}</p>
            <p className="text-xs text-muted-foreground mb-2">{rec.handle} · {rec.city}</p>
            <PlatformIcons platforms={rec.platforms} size="sm" />

            <div className="grid grid-cols-2 gap-2 my-3">
              <div className="bg-muted/50 rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Followers</p>
                <p className="text-sm font-700 font-mono text-foreground">{rec.followers}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Engagement</p>
                <p className="text-sm font-700 font-mono text-positive">{rec.engagement}%</p>
              </div>
            </div>

            <div className="space-y-1 mb-3">
              {rec.matchReasons.map((reason) =>
            <div key={`reason-${rec.id}-${reason}`} className="flex items-center gap-1.5">
                  <Icon name="CheckCircleIcon" size={12} className="text-positive flex-shrink-0" />
                  <span className="text-xs text-muted-foreground">{reason}</span>
                </div>
            )}
            </div>

            {rec.note &&
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-2 mb-3">
                <p className="text-xs text-warning">{rec.note}</p>
              </div>
          }

            <div className="bg-muted/50 rounded-lg p-2 mb-3">
              <p className="text-xs text-muted-foreground">Allocation</p>
              <p className="text-xs font-600 text-foreground">{rec.contentAlloc}</p>
            </div>

            <Link
            href="/creator-professional-page"
            className="w-full flex items-center justify-center gap-2 bg-muted hover:bg-border text-foreground text-xs font-600 py-2 rounded-lg transition-all">
            
              Voir profil complet
              <Icon name="ArrowRightIcon" size={12} />
            </Link>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <button
          onClick={onReset}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          
          <Icon name="ArrowLeftIcon" size={16} />
          Modifier le brief
        </button>
        <div className="flex items-center gap-3">
          <Link
            href="/brand-portal"
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-600 px-5 py-2.5 rounded-lg transition-all active:scale-95">
            
            <Icon name="BuildingOffice2Icon" size={16} />
            Voir dans Brand Portal
          </Link>
        </div>
      </div>
    </div>);

}